import { DealState } from '@enums/dealSteps';
import moment from 'moment';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { requestsMixin } from '@tools/requests-mixin';
import { eventBus } from '@tools/event-bus';
import { translationMixin } from '@elements/translation';
import { URL_XHR_DEAL_VIEW_PHONE_NUMBER } from '@tools/routes/routes';
import { Logger } from '@tools/logger/logger';

export const messagingMixins = {
  mixins: [ translationMixin, requestsMixin ],
  data() {
    return {
      formErrors: {},
      datepickerIsOpened: false,
      timepickerIsOpened: false,
    };
  },
  methods: {
    toggleHeader() {
      const header = document.querySelector('.header');

      if (window.BREAKPOINTS.isMobile && header) {
        header.classList.add('header--mobile--disabled');
      } else if (!window.BREAKPOINTS.isMobile && header) {
        header.classList.remove('header--mobile--disabled');
      }
    },
    resetToasterPadding() {
      document.getElementById('chat-bottom').style.marginTop = 0;
    },
    /**
     * Make the whole date picker visible when opening it
     */
    async onCalendarOpened(opened) {
      this.datepickerIsOpened = opened;
      await this.$nextTick();
      try {
        document.querySelector(opened ? '.vdp-datepicker__calendar' : '.vdp-datepicker').scrollIntoView(true);
      } catch (e) { /* empty */ }
    },
    /**
     * Make the whole time picker visible when opening it
     */
    async onTimeOpened(element, opened) {
      this.timepickerIsOpened = opened;
      await this.$nextTick();
      if (element) {
        element.scrollIntoView(true);
      }
    },
    showIcsInvite(actionType, actionPayload = {}) {
      const event = new CustomEvent('messaging-add-to-calendar', {
        detail: { actionName: actionType, actionPayload },
      });
      eventBus.dispatchEvent(event);
    },

    showAction(actionType, actionPayload = {}) {
      const event = new CustomEvent('messaging-action-show', {
        detail: { actionName: actionType, actionPayload },
      });
      eventBus.dispatchEvent(event);
    },

    hideAction() {
      eventBus.dispatchEvent(new Event('messaging-action-hide'));
    },

    toggleConversationDetailsBlock(isDisplayed) {
      const event = new CustomEvent('inbox-conversation-details-block-toggle', {
        detail: isDisplayed,
      });
      eventBus.dispatchEvent(event);
    },

    updateMessagingView(data) {
      const updateViewEvent = new CustomEvent('messaging-update-view', {
        detail: data,
      });
      const detailsUpdateViewEvent = new CustomEvent('details-update-view', {
        detail: data,
      });
      eventBus.dispatchEvent(updateViewEvent);
      eventBus.dispatchEvent(detailsUpdateViewEvent);
    },

    removeDealFromInboxList(dealId) {
      const event = new CustomEvent('input-list-remove-deal', {
        detail: dealId,
      });
      eventBus.dispatchEvent(event);
    },

    closeInboxItemDropdown(deal) {
      const decisionEvent = new CustomEvent('inbox-list-close-dropdown-decision', {
        detail: deal,
      });
      const closeEvent = new CustomEvent('inbox-list-close-dropdown', {
        detail: deal,
      });
      eventBus.dispatchEvent(decisionEvent);
      eventBus.dispatchEvent(closeEvent);
    },
    hasFormError(fieldName) {
      return this.formErrors[fieldName] !== undefined;
    },
    getFormError(fieldName) {
      return Array.isArray(this.formErrors[fieldName]) ? this.formErrors[fieldName][0] : this.formErrors[fieldName];
    },
    setFormError(fieldName, error) {
      this.formErrors[fieldName] = error;
    },
    resetFormErrors() {
      this.formErrors = {};
    },
    canDisplayInterlocutorDetails(dealState) {
      return ![ DealState.CONTACTED, DealState.AWAITING_MATCH_VALIDATION ].includes(dealState);
    },
    interlocutorShortName(deal, author) {
      if (!deal || !author) {
        return '';
      }

      if (deal.buyer.id === author.id) {
        return this.getUserShortName(deal.buyer);
      }

      return this.getUserShortName(deal.supervisor ?? author);
    },
    getUserShortName(user) {
      return user.status === 'deleted' ? this.trans('user.deleted.name')
        : user.short_name;
    },
    getUserFullName(user) {
      return user.status === 'deleted' ? this.trans('user.deleted.name')
        : user.full_name;
    },
    getInterlocutorFirstname(deal) {
      return window.initialAppState.displayed_timeline.is_buyer ? deal.supervisor.firstname : deal.buyer.firstname;
    },
    interlocutorFullName(deal, isSeller) {
      const seller = deal.supervisor ?? deal.seller;
      // Display seller short name for the buyer when the deal is still in "contacted" state
      if (!this.canDisplayInterlocutorDetails(deal.state)) {
        return isSeller ? '' : this.getUserShortName(seller);
      }

      return isSeller ? this.getUserFullName(deal.buyer) : this.getUserFullName(seller);
    },
    emitFlashbagGlobalError() {
      flashBagError();
    },
    emitFlashbagSuccessMessage(message) {
      flashBagSuccess(message);
    },
    getCancelVisitUrlParameter() {
      const urlParams = new URLSearchParams(window.location.search);

      return urlParams.get('cancel-visit');
    },
    getDisplayOnMobileUrlParameter() {
      const urlParams = new URLSearchParams(window.location.search);

      return urlParams.get('display-on-mobile');
    },
    getRescheduleUrlParameter() {
      const urlParams = new URLSearchParams(window.location.search);

      return urlParams.get('reschedule');
    },
    getFeedbackUrlParameter() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('feedback');
    },
    getProposeSlotUrlParameter() {
      const urlParams = new URLSearchParams(window.location.search);

      return urlParams.get('display-propose-slot');
    },
    async triggerTracking(dealId, isMobile) {
      const url = URL_XHR_DEAL_VIEW_PHONE_NUMBER.replace('{id}', dealId);

      await this
        .post(url, { is_mobile: isMobile })
        .catch(() => Logger.log('error catched'));
    },
    isAlreadyBooked(date) {
      const alreadyBookedSlots = window.initialAppState.displayed_timeline.booked_slots;
      let isAlreadyBooked = false;
      if (!alreadyBookedSlots) {
        return false;
      }

      alreadyBookedSlots.forEach((bookedSlot) => {
        if (this.isSame(bookedSlot.iso, date)) {
          isAlreadyBooked = true;
        }
        const slotBefore = moment(bookedSlot.iso);
        slotBefore.subtract(15, 'minutes');
        if (this.isSame(slotBefore, date)) {
          isAlreadyBooked = true;
        }
        const slotAfter = moment(bookedSlot.iso);
        slotAfter.add(15, 'minutes');
        if (this.isSame(slotAfter, date)) {
          isAlreadyBooked = true;
        }
      });
      return isAlreadyBooked;
    },
    getScrollRef(name, device = null) {
      let ref = name;
      if (device) {
        ref = ref.concat(`-${device}`);
      }

      return ref;
    },
  },
  computed: {
    isDealSeller() {
      return window.initialAppState.org_id !== this.deal.first_office_offer.author.organization.id;
    },
    isAccountManagerHuman() {
      return !!this.accountManager?.lastname;
    },
  },
};
